import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { resizeProfilePicture } from '../utils/imageResize';
import { clearUserData } from '../utils/offlineDb';
import { isNative, setNativeToken, clearNativeToken } from '../utils/nativeAuth';
import { clearGhinCredentials } from '../utils/ghinCredentials';
import Swal from 'sweetalert2';
import { swalConfirm } from '../utils/swalTheme';


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export useUser for backwards compatibility with existing components
export const useUser = () => {
  const { user, roles, hasRole, login, logout } = useAuth();
  return { user, roles, hasRole, login, logout };
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [roles, setRoles] = useState(['guest']); // Preserve existing role system
  const [userVersion, setUserVersion] = useState(0); // Force re-renders when user data changes
  const [sessionExpired, setSessionExpired] = useState(false); // Track if session was forcefully expired

  // Refs to prevent duplicate notifications and track auth state
  const sessionExpiredNotificationShown = useRef(false);
  const wasAuthenticated = useRef(false);
  const authCheckVersion = useRef(0); // Guards against stale checkAuthStatus calls

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Track when user becomes authenticated (for session expiry detection)
  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true;
      sessionExpiredNotificationShown.current = false;
      setSessionExpired(false);
    }
  }, [isAuthenticated]);

  // Listen for session expiration events from apiClient
  useEffect(() => {
    const handleSessionExpired = () => {
      // Only show notification if user was previously authenticated and we haven't shown it yet
      if (wasAuthenticated.current && !sessionExpiredNotificationShown.current) {
        sessionExpiredNotificationShown.current = true;
        if (process.env.NODE_ENV !== 'production') console.warn('Session expired - clearing auth state');

        // Clear auth state
        setUser(null);
        setIsAuthenticated(false);
        setRoles(['guest']);
        setSessionExpired(true);
        localStorage.removeItem("roles");
        if (isNative()) {
          clearNativeToken().catch(() => {});
        }
        wasAuthenticated.current = false;

        // Show user-friendly notification
        Swal.fire({
          ...swalConfirm,
          icon: 'info',
          title: 'Session Expired',
          text: 'Your session has expired. Please sign in again to continue.',
          confirmButtonText: 'Sign In',
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            // Dispatch event for components that want to show login modal
            window.dispatchEvent(new CustomEvent('auth:show-login'));
          }
        });
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Validate session on tab focus (catches expired sessions proactively)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only check if tab becomes visible and user was authenticated
      if (document.visibilityState === 'visible' && wasAuthenticated.current && isAuthenticated) {
        try {
          await apiClient.get('/auth/validate');
          // Session still valid, no action needed
        } catch (error) {
          // Session invalid - the apiClient interceptor will dispatch auth:session-expired
          if (process.env.NODE_ENV !== 'production') console.warn('Session validation failed on tab focus');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // Listen for deep link callbacks from native OAuth (iOS: SFSafariViewController)
  // and browser close events (Android: Chrome Custom Tabs uses cookie flow)
  useEffect(() => {
    if (!isNative()) return;

    let deepLinkCleanup;
    let browserCleanup;

    const handleAuthCallback = async (url) => {
      if (!url) return;

      const parseCustomScheme = (u) =>
        new URL(u.replace('com.gulfcoasthackers.league://', 'https://placeholder/'));

      // Handle custom-scheme OAuth callback (Google/Apple sign-in)
      if (url.startsWith('com.gulfcoasthackers.league://auth/callback')) {
        const parsed = parseCustomScheme(url);
        const token = parsed.searchParams.get('token');
        if (token) {
          await setNativeToken(token);
          const { Browser } = await import('@capacitor/browser');
          try { await Browser.close(); } catch { /* may already be closed */ }
          await checkAuthStatus();
        }
        return;
      }

      // Handle magic link verification inside the app
      // Safari redirects here with the raw magic token so verification
      // happens in the app's WKWebView context (cookies + Bearer token both work)
      if (url.startsWith('com.gulfcoasthackers.league://auth/magic-verify')) {
        const parsed = parseCustomScheme(url);
        const magicToken = parsed.searchParams.get('token');
        const email = parsed.searchParams.get('email');
        if (magicToken && email) {
          await verifyMagicLink(magicToken, email);
        }
        return;
      }

      // Handle Universal Links (https://gulfcoasthackers.com/...)
      if (url.startsWith('https://gulfcoasthackers.com/')) {
        const parsed = new URL(url);

        // Magic link Universal Link: verify directly instead of navigating
        // to MagicLinkVerification page (avoids navigation-related state loss)
        if (parsed.pathname === '/auth/verify' || parsed.pathname === '/auth/magic') {
          const magicToken = parsed.searchParams.get('token');
          const email = parsed.searchParams.get('email');
          if (magicToken && email) {
            await verifyMagicLink(magicToken, email);
            return;
          }
        }

        const path = parsed.pathname + parsed.search;
        navigate(path);
      }
    };

    const setup = async () => {
      const { App: CapApp } = await import('@capacitor/app');
      const { Browser } = await import('@capacitor/browser');

      // Handle cold-start: app launched via deep link before listener was registered
      const launchUrl = await CapApp.getLaunchUrl();
      if (launchUrl?.url) {
        await handleAuthCallback(launchUrl.url);
      }

      // iOS deep link: custom URL scheme callback with token
      deepLinkCleanup = await CapApp.addListener('appUrlOpen', async (event) => {
        await handleAuthCallback(event.url);
      });

      // Android cookie flow: when Chrome Custom Tabs closes, re-check auth
      // (On Android, cookies set in Chrome Custom Tabs are shared with WebView)
      browserCleanup = await Browser.addListener('browserFinished', async () => {
        await checkAuthStatus();
      });
    };

    setup();

    return () => {
      if (deepLinkCleanup) deepLinkCleanup.remove();
      if (browserCleanup) browserCleanup.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = useCallback(async () => {
    // Increment version so stale calls (e.g. initial mount 401 racing against
    // a deep link login) don't overwrite a newer successful auth check
    const callVersion = ++authCheckVersion.current;

    try {
      const response = await apiClient.get('/auth/validate');

      // If a newer checkAuthStatus was initiated while we were waiting, discard this result
      if (callVersion !== authCheckVersion.current) return;

      // Set basic user data from validate endpoint
      setUser(response.data.user);
      setIsAuthenticated(true);

      // Now fetch the full profile data which includes linked player info
      try {
        const profileResponse = await apiClient.get('/auth/profile');
        if (callVersion !== authCheckVersion.current) return;

        const profileUser = profileResponse.data.user || profileResponse.data;

        // Update user with complete profile data including player info
        const fullUserData = {
          ...response.data.user,
          ...profileUser,
          playerId: profileUser.playerId || response.data.user?.playerId || null,
          player: profileUser.player || null,
        };

        setUser(fullUserData);
      } catch (profileError) {
        // Continue with basic user data if profile fetch fails
      }

      setUserVersion(prev => prev + 1); // Force re-renders

      // Preserve existing role system
      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

    } catch (error) {
      // Only clear auth state if this is still the latest call
      if (callVersion !== authCheckVersion.current) return;
      setUser(null);
      setIsAuthenticated(false);
      setRoles(['guest']);
      localStorage.removeItem("roles");
    } finally {
      setLoading(false);
    }
  }, []);

  // Backwards compatibility - support old login method
  const legacyLogin = (newRoles) => {
    setRoles(newRoles);
    localStorage.setItem("roles", JSON.stringify(newRoles));
  };

  const login = async (emailOrUsername, password, isAdminLogin = false) => {
    try {
      const requestBody = isAdminLogin
        ? { username: emailOrUsername, password }
        : { email: emailOrUsername, password };

      const response = await apiClient.post('/auth/login', requestBody);

      setUser(response.data.user);
      setIsAuthenticated(true);

      // Update roles
      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

      // Refresh user data to get complete profile with player info
      await checkAuthStatus();

      // Force re-renders in components that depend on user data
      setUserVersion(prev => prev + 1);

      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    try {
      const response = await apiClient.post('/auth/register', {
        email,
        password,
        firstName,
        lastName
      });

      setUser(response.data.user);
      setIsAuthenticated(true);

      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

      // Refresh user data to get complete profile with player info
      await checkAuthStatus();

      // Force re-renders in components that depend on user data
      setUserVersion(prev => prev + 1);

      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  };

  const requestMagicLink = async (email, redirectTo = null) => {
    try {
      await apiClient.post('/auth/magic-link', { email, redirectTo });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send magic link'
      };
    }
  };

  const verifyMagicLink = async (token, email) => {
    try {
      const response = await apiClient.post('/auth/magic-verify', {
        token,
        email
      });

      const jwt = response.data.token;

      // Store the JWT for Bearer auth (handles native where cookies don't work)
      if (jwt) {
        await setNativeToken(jwt);
      }

      setUser(response.data.user);
      setIsAuthenticated(true);

      const userRoles = response.data.user?.roles || ['player'];
      setRoles(userRoles);
      localStorage.setItem("roles", JSON.stringify(userRoles));

      // Refresh user data to get complete profile with player info
      await checkAuthStatus();

      return { success: true, user: response.data.user, token: jwt };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Invalid or expired magic link'
      };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout', {});
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Logout error:', error);
    } finally {
      // Clear offline data for security (prevents cross-user data exposure)
      try {
        await clearUserData();
      } catch {
        // Ignore errors - user data will be orphaned but not accessible
      }
      // Clear native token storage
      if (isNative()) {
        try { await clearNativeToken(); } catch { /* ignore */ }
      }
      // GHIN login lives on the device; it must not outlive the league session.
      try { await clearGhinCredentials(user?.id); } catch { /* ignore */ }
      setUser(null);
      setIsAuthenticated(false);
      setRoles(['guest']);
      localStorage.removeItem("roles");
    }
  };

  const linkPlayer = async (playerId) => {
    try {
      // Convert playerId to number (select elements return strings)
      const response = await apiClient.post('/auth/link-player', { playerId: parseInt(playerId, 10) });

      if (response.data?.message) {
        // Refresh user data after linking to get updated profile with player info
        await checkAuthStatus();

        // Force re-renders in components that depend on user data
        setUserVersion(prev => prev + 1);

        return { success: true };
      }

      return { success: false, error: 'Failed to link player' };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to link player'
      };
    }
  }; const updateProfile = async (profileData) => {
    try {
      const response = await apiClient.put('/auth/profile', profileData);

      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update profile'
      };
    }
  };

  const uploadProfilePicture = async (file) => {
    try {
      // Resize image client-side before upload (max 400x400 for profile pictures)
      let fileToUpload = file;
      try {
        fileToUpload = await resizeProfilePicture(file, 400, 0.85);
      } catch (resizeError) {
        console.warn('Failed to resize profile picture, using original:', resizeError);
        // Continue with original file if resize fails
      }

      const formData = new FormData();
      formData.append('profilePicture', fileToUpload);

      const response = await apiClient.post('/auth/profile/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update user data with new profile picture
      setUser(prev => ({
        ...prev,
        profilePicture: response.data.profilePicture
      }));

      // Force re-renders in components that depend on user data
      setUserVersion(prev => prev + 1);

      return { success: true, profilePicture: response.data.profilePicture };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload profile picture'
      };
    }
  };

  const getProfile = async () => {
    try {
      const response = await apiClient.get('/auth/profile');
      return { success: true, profile: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to get profile'
      };
    }
  };

  const signUpForEvent = async (eventId) => {
    try {
      const response = await apiClient.post(`/events/${eventId}/signup`, {});
      return {
        success: true,
        message: response.message,
        isBirthday: response.data?.isBirthday || false,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to sign up for event'
      };
    }
  };

  const withdrawFromEvent = async (eventId) => {
    try {
      const response = await apiClient.delete(`/events/${eventId}/signup`);
      return { success: true, message: response.message };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to withdraw from event'
      };
    }
  };

  // Role checking function (preserves existing functionality)
  const hasRole = (role) => roles.includes(role);

  const value = {
    // New auth system
    user,
    isAuthenticated,
    loading,
    userVersion, // Force re-renders when user data changes
    sessionExpired, // True if session was forcefully expired (for UI feedback)
    login,
    register,
    logout,
    requestMagicLink,
    verifyMagicLink,
    linkPlayer,
    updateProfile,
    uploadProfilePicture,
    getProfile,
    signUpForEvent,
    withdrawFromEvent,
    checkAuthStatus,

    // Backwards compatibility for existing components
    roles,
    hasRole,
    legacyLogin, // Support old login method
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
